import { BookingEventForm, type BookingEventFormState, type BookingEventSubmission } from "./BookingEventForm";

type BookingEventDrawerProps = {
  open: boolean;
  initialValues?: Partial<BookingEventFormState>;
  submitting?: boolean;
  onSubmit: (values: BookingEventSubmission) => Promise<void>;
  onClose: () => void;
  queueIndex?: number;
  queueTotal?: number;
  onPrev?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
};

export function BookingEventDrawer({
  open,
  initialValues,
  submitting,
  onSubmit,
  onClose,
  queueIndex,
  queueTotal,
  onPrev,
  onNext,
  onSkip,
}: BookingEventDrawerProps) {
  if (!open) return null;

  const metadata = (initialValues?.metadata ?? {}) as Record<string, unknown>;
  const linkedLeadId = metadata?.crm_lead_id as number | string | undefined;
  const linkedTitle = (metadata?.lead_title as string) || initialValues?.event_name;
  const linkedRegion = metadata?.lead_region as string | undefined;
  const linkedOwner = metadata?.lead_owner as string | undefined;
  const bookingScore =
    typeof metadata?.booking_score === "number"
      ? Number(metadata.booking_score).toFixed(2)
      : undefined;
  const contactLabel = metadata?.booking_contact_label as string | undefined;

  return (
    <div className="fixed inset-0 z-40" aria-hidden={!open}>
      <div
        className="absolute inset-0 bg-black/60 transition-opacity opacity-100"
        onClick={onClose}
      />
      <aside
        className="absolute right-0 top-0 h-full w-full max-w-lg transform bg-background/95 text-foreground/80 shadow-2xl transition-transform translate-x-0"
      >
        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-amber-300/70">Create event</p>
            <h2 className="text-lg font-semibold text-foreground sm:text-xl">Create an event</h2>
            {typeof queueIndex === "number" && typeof queueTotal === "number" && queueTotal > 1 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Event {queueIndex + 1} of {queueTotal}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {onPrev && (
              <button
                type="button"
                onClick={onPrev}
                className="rounded-full border border-border px-2.5 py-1 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
                disabled={queueIndex === 0}
              >
                Prev
              </button>
            )}
            {onNext && (
              <button
                type="button"
                onClick={onNext}
                className="rounded-full border border-border px-2.5 py-1 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
                disabled={typeof queueTotal === "number" && typeof queueIndex === "number" && queueIndex >= queueTotal - 1}
              >
                Next
              </button>
            )}
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                className="rounded-full border border-border px-2.5 py-1 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
              >
                Skip
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-border px-2.5 py-1 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
        </div>
        <div className="h-[calc(100%-4rem)] overflow-y-auto px-4 py-4 space-y-4 sm:px-6 sm:py-5">
          {(linkedLeadId || bookingScore || contactLabel) && (
            <section className="rounded-2xl border border-border bg-background/60 p-4 text-sm text-foreground/70">
              <p className="text-xs uppercase tracking-wide text-amber-300">Linked opportunity</p>
              <dl className="mt-2 space-y-1 text-xs">
                {linkedLeadId && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">CRM lead</dt>
                    <dd className="text-foreground/80">
                      #{linkedLeadId} · {linkedTitle || "Untitled"}
                    </dd>
                  </div>
                )}
                {contactLabel && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Venue</dt>
                    <dd className="text-foreground/80">{contactLabel}</dd>
                  </div>
                )}
                {linkedRegion && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Region</dt>
                    <dd className="text-foreground/80">{linkedRegion}</dd>
                  </div>
                )}
                {linkedOwner && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Owner</dt>
                    <dd className="text-foreground/80">{linkedOwner}</dd>
                  </div>
                )}
                {bookingScore && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Booking score</dt>
                    <dd className="text-primary-foreground dark:text-amber-200">{bookingScore}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          <section className="rounded-2xl border border-border bg-background/60 p-4 text-xs text-muted-foreground">
            <p className="text-primary-foreground dark:text-amber-200">What happens after you create the event</p>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              <li>We add the show to your Shows page and link it to this booking.</li>
              <li>The booking is marked Booked and kept as a record of this opportunity.</li>
              <li>Nothing is published or sent anywhere else.</li>
            </ul>
          </section>

          <BookingEventForm
            initialValues={initialValues}
            submitting={submitting}
            submitLabel="Create event"
            onSubmit={onSubmit}
            resetAfterSubmit
          />
        </div>
      </aside>
    </div>
  );
}
